import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { TokenCreate, TokenResponse } from "@/lib/types";
import { useParams } from "react-router-dom";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { useTokensStore } from "@/stores/tokensStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Copy, Check, X, Edit2 } from "lucide-react";
import InboundCallSettings from "@/components/InboundCallSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SecurityToken {
  id: string;
  name: string;
  createdAt: Date;
  expiresAt: Date | null;
  isActive: boolean;
  accessToken: string | null;
}

export default function SettingsPage() {
  const { selectedOrganizationId } = useOrganizationsStore();

  const {
    tokensByOrg,
    setTokens,
    fetchTokensForCurrentOrg,
    addToken,
    deleteToken,
    getToken,
  } = useTokensStore();

  const [tokensLocal, setTokensLocal] = useState<SecurityToken[]>([]);
  const [newTokenDetails, setNewTokenDetails] = useState<TokenResponse | null>(
    null
  );
  const [editingTokenId, setEditingTokenId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [activeTab, setActiveTab] = useState("tokens");

  // First useEffect to fetch tokens once when organization changes
  useEffect(() => {
    if (selectedOrganizationId) {
      fetchTokensForCurrentOrg();
    }
  }, [selectedOrganizationId]);

  // Second useEffect to format tokens when tokensByOrg changes
  useEffect(() => {
    const tokens = selectedOrganizationId
      ? [...(tokensByOrg[selectedOrganizationId] || [])].reverse()
      : [];

    const formattedTokens: SecurityToken[] = tokens.map((token) => ({
      id: token.id,
      name: token.name,
      createdAt: new Date(token.created_at),
      expiresAt: token.expires_at ? new Date(token.expires_at) : null,
      isActive:
        token.is_active === false
          ? false
          : token.expires_at
          ? new Date(token.expires_at) > new Date()
          : true,
      accessToken: token.access_token,
    }));

    setTokensLocal(formattedTokens);
  }, [selectedOrganizationId, tokensByOrg]);

  const handleCreateToken = async () => {
    if (!selectedOrganizationId) return;

    try {
      const tokenData: TokenCreate = {
        name: `Token ${tokensLocal.length + 1}`,
        organization_id: selectedOrganizationId,
        expires_in_days: 365,
      };

      const token = await useTokensStore.getState().generateToken(tokenData);
      setNewTokenDetails(token);
    } catch (error) {
      console.error("Failed to create token:", error);
    }
  };

  const handleDeactivateToken = async (tokenId: string) => {
    try {
      await useTokensStore.getState().revokeToken(tokenId);
    } catch (error) {
      console.error("Failed to deactivate token:", error);
    }
  };

  const handleDialogClose = () => {
    setNewTokenDetails(null);
  };

  const handleEditStart = (token: SecurityToken) => {
    setEditingTokenId(token.id);
    setEditingName(token.name);
  };

  const handleEditCancel = () => {
    setEditingTokenId(null);
    setEditingName("");
  };

  const handleEditSave = async (tokenId: string) => {
    try {
      await useTokensStore
        .getState()
        .updateToken(tokenId, { name: editingName });
      setEditingTokenId(null);
    } catch (error) {
      console.error("Failed to update token name:", error);
    }
  };

  return (
    <div className="container mx-auto py-10 pl-1">
      <h1 className="text-2xl font-bold mb-8">Settings</h1>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="flex justify-start p-6">
          <TabsTrigger value="tokens">Security Tokens</TabsTrigger>
          <TabsTrigger value="inbound-calls">Inbound Calls</TabsTrigger>
        </TabsList>

        <TabsContent value="tokens" className="p-6 bg-slate-50 ml-4 rounded-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Security Tokens</h2>
            <Button onClick={handleCreateToken}>Create New Token</Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokensLocal.map((token) => (
                <TableRow key={token.id}>
                  <TableCell>
                    {editingTokenId === token.id ? (
                      <div className="flex gap-2 items-center">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="h-8"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEditSave(token.id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={handleEditCancel}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        {token.name}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEditStart(token)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{format(token.createdAt, "PPP")}</TableCell>
                  <TableCell>
                    {token.expiresAt ? format(token.expiresAt, "PPP") : "Never"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-sm ${
                        token.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {token.isActive ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {token.isActive && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeactivateToken(token.id)}
                      >
                        Deactivate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tokensLocal.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    No tokens found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent
          value="inbound-calls"
          className="p-6 bg-slate-50 rounded-lg ml-4"
        >
          {selectedOrganizationId && (
            <InboundCallSettings organizationId={selectedOrganizationId} />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={newTokenDetails !== null} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Access Token Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-red-600">
              Important: Copy this access token now. You won't be able to see it
              again!
            </p>
            <div className="flex gap-2">
              <Input
                value={newTokenDetails?.access_token || ""}
                readOnly
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(
                    newTokenDetails?.access_token || ""
                  );
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
